import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { formatMetric, type AnalysisData } from './analysis.shared';

/**
 * Figma 97:3541 card, 97:3549 BarChart (pad 10, [tooltip 22 | gap 10 | Icon]),
 * 97:3553 Icon (py 6), 97:3554 chart+axis (gap 9), meal rows gap 18, label–bars gap 8.
 */
const MEAL_BAR = {
  teal: '#2ec3b6',
  cyan: '#20aedc',
  blue: '#3f9fee',
  legendDot: '#20aedc',
} as const;

/** BarChart: reserved tooltip column + gap before Icon (97:3549). */
const TOOLTIP_SLOT_PX = 22;
const TOOLTIP_GAP_PX = 10;

/** Figma 97:6122 — per-meal legend dots on Macros tab only. */
export type EnergyByMealCardAppearance = 'default' | 'macroFigma';

interface EnergyByMealCardProps {
  analysisData: AnalysisData;
  appearance?: EnergyByMealCardAppearance;
  /** 1 = By the plan (viewMode) — title/subtitle and rows reflect energy by plan day (see merge). */
  viewMode?: number;
}

function roundScaleMax(value: number): number {
  if (value <= 0) return 100;
  const rounded = Math.ceil(value / 100) * 100;
  return rounded < 100 ? 100 : rounded;
}

function mealBarColor(mealKcal: number, maxMealEnergy: number, index: number): string {
  if (mealKcal > 0 && mealKcal === maxMealEnergy) return MEAL_BAR.teal;
  return index % 2 === 0 ? MEAL_BAR.cyan : MEAL_BAR.blue;
}

function formatTwoDecimals(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return rounded.toFixed(2);
}

export const EnergyByMealCard: React.FC<EnergyByMealCardProps> = ({
  analysisData,
  appearance = 'default',
  viewMode = 0,
}) => {
  const isMacroFigma = appearance === 'macroFigma';
  const isCumulative = viewMode === 1;
  const maxMealEnergy = Math.max(...analysisData.energyByMeal.map((entry) => entry.kcal), 1);
  const scaleMax = roundScaleMax(maxMealEnergy);
  const xAxisTicks = [0, Math.round(scaleMax / 2), scaleMax];

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '14px',
        border: '1px solid #f3f4f6',
        bgcolor: '#fff',
        p: '21px',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', flexShrink: 0 }}>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: '18px',
            lineHeight: '27px',
            letterSpacing: '-0.44px',
            color: '#1e2939',
          }}
        >
          {isCumulative ? 'Energy by Day' : 'Energy by Meal Occasion'}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '16px',
            color: '#99a1af',
          }}
        >
          {isCumulative ? 'Total plan' : 'Total today'}: {formatMetric(analysisData.energy, 0)} kcal
        </Typography>
      </Box>

      <Box
        sx={{
          mt: '16px',
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}
      >
        {/* 97:3549 BarChart — padding 10; horizontal [tooltip | gap 10 | Icon] */}
        <Box
          sx={{
            p: '10px',
            flexShrink: 0,
            minWidth: 0,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'stretch',
            gap: { xs: 0, sm: `${TOOLTIP_GAP_PX}px` },
          }}
        >
          <Box
            sx={{
              display: { xs: 'none', sm: 'block' },
              width: TOOLTIP_SLOT_PX,
              flexShrink: 0,
            }}
            aria-hidden
          />
          {/* 97:3553 Icon — py 6 */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              pt: '6px',
              pb: '6px',
              display: 'flex',
              flexDirection: 'column',
              gap: '9px',
            }}
          >
            {/* 97:3555 meal + bar block */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px', minWidth: 0 }}>
              {analysisData.energyByMeal.map((meal, index) => (
                <Box
                  key={`${meal.name}-${index}`}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: { xs: '8px', sm: '8px' },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '13px',
                      color: '#6b7280',
                      textAlign: { xs: 'left', sm: 'right' },
                      flexShrink: 0,
                      width: { sm: 72 },
                      minWidth: { sm: 72 },
                      maxWidth: { sm: 72 },
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {meal.name}
                  </Typography>

                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        height: 30,
                        borderRadius: '8px',
                        bgcolor: '#f3f4f6',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          height: '100%',
                          width: `${Math.max(0, Math.min(100, (meal.kcal / maxMealEnergy) * 100))}%`,
                          minWidth: meal.kcal > 0 ? 2 : 0,
                          bgcolor: mealBarColor(meal.kcal, maxMealEnergy, index),
                          borderRadius: '8px',
                          transition: 'width 180ms ease-out',
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '13px',
                        color: '#6b7280',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        minWidth: { sm: 52 },
                        textAlign: 'right',
                      }}
                    >
                      {formatTwoDecimals(meal.kcal)} kcal
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Axis row aligned to the same bar-track width as meal rows. */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'flex-end',
                gap: { sm: '8px' },
                minWidth: 0,
              }}
            >
              <Box sx={{ display: { xs: 'none', sm: 'block' }, width: 72, minWidth: 72, flexShrink: 0 }} />
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: '5px',
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={{ width: '100%', height: 0, borderTop: '1px solid #e5e7eb', mt: '2px' }} />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: '6px',
                    }}
                  >
                    {xAxisTicks.map((tick) => (
                      <Typography
                        key={tick}
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 400,
                          fontSize: '12px',
                          lineHeight: '12px',
                          color: '#9ca3af',
                          textAlign: 'center',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {tick}
                      </Typography>
                    ))}
                  </Box>
                </Box>
                <Box sx={{ display: { xs: 'none', sm: 'block' }, width: 52, flexShrink: 0 }} aria-hidden />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* 97:3607 legend — sibling of BarChart, gap 12 from parent */}
        <Box
          sx={{
            flexShrink: 0,
            borderTop: '1px solid #f3f4f6',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            columnGap: '12px',
            rowGap: '8px',
            width: '100%',
          }}
        >
          {analysisData.energyByMeal.map((meal, index) => (
            <Box
              key={`energy-legend-${meal.name}-${index}`}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}
            >
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                <circle cx="4" cy="4" r="4" fill={isMacroFigma ? mealBarColor(meal.kcal, maxMealEnergy, index) : MEAL_BAR.legendDot} />
              </svg>
              <Typography
                component="span"
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '15px',
                  letterSpacing: '0.1172px',
                  color: '#6a7282',
                }}
              >
                {meal.name}{' '}
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '12px',
                  lineHeight: '15px',
                  letterSpacing: '0.1172px',
                  color: '#6a7282',
                }}
              >
                {formatTwoDecimals(meal.percent)}%
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Paper>
  );
};
